import {basicPromotion} from './basicPromotion.js'
import {BASIC_BED_PRICE, BASIC_CUSTOM_COLOR_PRICE} from './basicBedPricing.js'
import {castlePackage} from './castlePackage.js'
import { cabinMillworkPricing } from './cabinMillworkPricing.js'
import {BASIC_UPGRADE_PRICES,BASIC_UPGRADE_LABELS,isBasicUpgradeSelections,type BasicUpgradeKey} from './basicUpgrades.js'
import { planReviewItems, PLAN_EXCLUSIONS } from './planPurchaseReview.js'
import { customFabricationTotal, customReserveBaseline } from './customProposalPricing.js'
import { roomDetailTakeoff } from '../src/components/bliss/design/configurator/room/custom-detail-takeoff.js'
import {
  guardrailOption,
  guardrailDescription,
  isBasicGuardrailStyle,
} from '../src/config/blissGuardrails.js'
import { tallestStatedSleeper } from './sleeperHeight.js'
import { customPaintUpcharge, roomFinishDescription, DEFAULT_ROOM_FINISH, requiresCustomFinish, hasCustomDetailUpgrades } from '../src/config/blissRoomFinish.js'
import type { PricingConfig } from '../src/types/pricing.js'
import {
  proposalTierTotal,
  BLISS_LUXURY_TIER_PRESENTATION,
} from '../src/config/blissLuxuryProposalCopy.js'
import { calculateBlissDeliveryFromZip } from '../src/utils/blissDeliveryDistance.js'
import {
  BEDSIDE_PRICING,
  isBedsideSelections,
  priceBedsideAddons,
  requiresCustomBedside,
  type BedsidePricing,
} from '../src/config/blissBedsideAddons.js'

export type ProductLine = 'basic' | 'custom'
export type PlanPackage = {
  id: string
  label: string
  total: number
  scope: string[]
  recommended: boolean
  image?: { src: string; alt: string; caption?: string }
  /** A fully priced variant including the optional room package. */
  basePackageId?: string
}
export type PlanProposal = {
  /** Owner-prepared prices for this quote only; never apply catalog promotions. */
  fixedQuotePrices?: boolean
  roomPackage?: { label: string; total: number; scope: string[] }
  promotion?: ReturnType<typeof basicPromotion>
  catalogModel?: 'castle'
  productLine: ProductLine
  planId: string
  packages: PlanPackage[]
  reasons: string[]
  summary: string
  rooms: number
  separatelyQuotedSelections?: string[]
  reviewItems?: string[]
  exclusions?: string[]
  deliveryZip?: string
  timing?: string[]
}
const CONFIGS: Record<string, string> = {
  'twin over twin': 'Twin/Twin',
  'twin xl over twin xl': 'Twin XL/Twin XL',
  'twin over full': 'Twin/Full',
  'twin xl over queen': 'Twin XL/Queen',
  'queen over queen': 'Queen/Queen',
  'full over full': 'Full/Full',
}
/** A multi-room order has one delivery destination. Never silently price a
 * different ZIP simply because the active room moved to the front of the list. */
export function planDeliveryZip(plan: any, fallback = ''): string {
  const rooms = plan.layout?.rooms?.length ? plan.layout.rooms : [{configuratorSnapshot: plan.layout?.configuratorSnapshot}]
  const zips = rooms.map((room: any) => room.configuratorSnapshot?.quoteSettings?.zip).filter((zip: unknown) => zip !== undefined && zip !== '')
  if (zips.some((zip: unknown) => typeof zip !== 'string' || !/^\d{5}$/.test(zip))) throw new Error('Enter a five-digit delivery ZIP.')
  const unique = [...new Set(zips)]
  if (unique.length > 1) throw new Error('Use the same delivery ZIP for every room on this quote.')
  return unique[0] as string || fallback
}
/** Price the saved rooms, never an amount supplied by the checkout browser. */
export function buildPlanPackages(
  plan: any,
  productLine: ProductLine,
  pricing: PricingConfig,
  zip: string,
  bedsidePricing: BedsidePricing = BEDSIDE_PRICING,
  options: { estimateOnly?: boolean; promoCode?: string } = {}
): PlanProposal {
  const castle = castlePackage(plan, productLine, zip);
  if (castle) { if(options.promoCode) throw new Error('BEDOCTOBER applies only to Bliss Basic base bunk sets.'); return castle; }
  const rooms = plan.layout?.rooms?.length
    ? plan.layout.rooms
    : [{ name: 'Your room', configuratorSnapshot: plan.layout?.configuratorSnapshot }]
  const reviewItems = planReviewItems(plan, bedsidePricing)
  if (reviewItems.length && !options.estimateOnly) throw new Error('Selected extras or finishes must be confirmed before a deposit. Request a completed quote. ' + reviewItems.join('; '))
  if (rooms.length > 20) throw new Error('This project needs a designer-prepared proposal.')
  const scope: string[] = [],
    standardGuardrails: string[] = [],
    extras: string[] = [],
    reasons: string[] = []
  let base = 0,
    addonTotal = 0,
    customGuardrailTotal = 0,
    customBedsideTotal = 0,
    customDetailTotal = 0,
    customPaintTotal = 0,
    customMillworkTotal = 0,
    needsPremier = false
  const addon = (key: string) => {
    const item = pricing.addOns?.[key]
    const amount = typeof item === 'number' ? item : item?.clientPrice
    if (!Number.isFinite(amount) || amount! < 0)
      throw new Error('An add-on needs a confirmed price. Please contact Bliss.')
    return amount!
  }
  let totalUnits = 0
  let goalsMet = true
  for (const [index, entry] of rooms.entries()) {
    const roomBaseStart = base, roomAddonStart = addonTotal
    const snap = entry.configuratorSnapshot
    if (!snap || !Array.isArray(snap.beds) || !snap.beds.length || snap.beds.length > 30)
      throw new Error('Complete your room layout before choosing a package.')
    if (snap.beds.some((b:any) => b.lowerShiplap !== undefined && typeof b.lowerShiplap !== 'boolean')) throw new Error('Review the accent wall shiplap selection.')
    if (snap.beds.some((b:any) => b.builtInBookshelf !== undefined && typeof b.builtInBookshelf !== 'boolean')) throw new Error('Review the built-in bookshelf selection.')
    const label = entry.name || `Room ${index + 1}`,
      names: string[] = []
    const bunkFinishes=snap.beds.filter((b:any)=>!b.kind);
    for(const [i,bed] of bunkFinishes.entries()){
      if(bed.basicFinish!==undefined&&!['matte_white','gloss_white','custom_color'].includes(bed.basicFinish))throw new Error(`${label}: choose Gloss White or Custom Color.`);
      if(bed.basicColorCode!==undefined&&(typeof bed.basicColorCode!=='string'||!/^SW\d{4}$/.test(bed.basicColorCode)))throw new Error(`${label}: enter a four-digit Sherwin-Williams code, such as SW1234.`);
      if(productLine==='basic'&&bed.basicFinish==='custom_color')base+=BASIC_CUSTOM_COLOR_PRICE;
      if(bed.basicFinish)scope.push(`${label}, bunk ${i+1}: ${bed.basicFinish==='custom_color'?`Custom Color${bed.basicColorCode?' · '+bed.basicColorCode:''} · $${BASIC_CUSTOM_COLOR_PRICE}`:bed.basicFinish==='gloss_white'?'Gloss White':'Matte White'}`);
    }
    const upgrades = snap.basicUpgrades ?? []
    if (!isBasicUpgradeSelections(upgrades,snap.beds)) throw new Error(`${label}: review your bunk upgrades and staircase assignments.`)
    if (upgrades.length && productLine !== 'basic') throw new Error('Basic upgrade selections require a Basic quote. Review Custom selections with your designer.')
    for(const selection of upgrades) for(const key of Object.keys(BASIC_UPGRADE_PRICES) as (keyof typeof BASIC_UPGRADE_PRICES)[]) {
      // Staircase geometry below owns its charge; do not charge it twice.
      if(key==='stairs'||!selection[key])continue
      needsPremier=true; addonTotal+=BASIC_UPGRADE_PRICES[key]
      const bunkNumber=snap.beds.filter((b:any)=>!b.kind).findIndex((b:any)=>b.id===selection.bedId)+1
      extras.push(`${label}, bunk ${bunkNumber}: ${BASIC_UPGRADE_LABELS[key]} · $${BASIC_UPGRADE_PRICES[key].toLocaleString('en-US')}`)
    }
    const bedsideSelections = snap.bedsideAddons ?? []
    if (!isBedsideSelections(bedsideSelections))
      throw new Error(`${label}: review your bedside add-on selections.`)
    if (productLine === 'basic' && requiresCustomBedside(bedsideSelections))
      throw new Error(
        'Recessed wall niches modify the room and require Bliss Custom. Continue with Bliss Custom.'
      )
    const bedside = priceBedsideAddons(bedsideSelections, snap.beds, bedsidePricing)
    if (bedside.pending && !options.estimateOnly)
      throw new Error(
        `${label}: bedside add-on prices and electrical allowances must be confirmed before choosing a package or paying a deposit.`
      )
    if (bedsideSelections.length) {
      needsPremier = true
      if (productLine === 'custom') customBedsideTotal += bedside.total
      else addonTotal += bedside.total
      extras.push(
        ...bedside.lines.map(
          line =>
            `${label}: ${line.label}${line.detail ? ` · ${line.detail}` : ''} · ${line.pending ? 'pending — excluded from this estimate' : '$' + line.price.toLocaleString('en-US')}`
        )
      )
      reasons.push(
        `${label}: ${productLine === 'basic' ? 'Premier includes' : 'Your Custom packages include'} your selected bedside storage and lighting.`
      )
    }
    for (const bed of snap.beds) {
      if (bed.stairStyle !== undefined && (!['enclosed', 'open', 'stringer'].includes(bed.stairStyle) || !(bed.kind === 'stairs' || bed.kind === 'bridge' || (bed.kind === 'custom' && ['rosemary', 'watercolor', 'scenic'].includes(bed.customStyle))))) throw new Error('Review the selected staircase style.')
      if (productLine === 'custom' && bed.stairStyle === 'stringer') extras.push(`${label}: ${bed.name} · exposed stringer stairs, two 4×4 support posts, open risers; no handrails or stair drawers; existing staircase allowance pending final design`)
      if (productLine === 'custom' && bed.stairStyle === 'open') extras.push(`${label}: ${bed.name} · open staircase sides without handrails · supported steps retained`)
    }
    let stairs = snap.beds.filter((b: any) => b.kind === 'stairs').length
    for (const bed of snap.beds) {
      if (bed.kind === 'stairs') continue
      if (bed.kind === 'bridge' || bed.kind === 'custom') {
        if (productLine === 'basic')
          throw new Error(
            'This built-in layout belongs in Bliss Custom. Choose Continue with Bliss Custom.'
          )
        const style = bed.kind === 'bridge' ? 'bridge' : bed.customStyle
        if (bed.mattressConfig !== undefined && (!['twin_full', 'twinxl_queen'].includes(bed.mattressConfig) || !['bridge', 'rosemary'].includes(style))) throw new Error('Review the selected mattress sizes.')
        if (!['bridge', 'rosemary', 'inlet', 'watercolor', 'scenic', 'divider'].includes(style))
          throw new Error('This custom layout needs a designer-prepared quote.')
        if (style === 'divider' && ((bed.upperBedSize !== undefined && !['twin','twinxl'].includes(bed.upperBedSize)) || (bed.lowerBedSize !== undefined && !['twin','full'].includes(bed.lowerBedSize)))) throw new Error('Review the cabin mattress sizes.')
        names.push(
          ...(style === 'divider'
            ? Array(2).fill(bed.upperBedSize === 'twinxl' ? (bed.lowerBedSize === 'twin' ? 'Twin XL/Twin XL' : 'Twin XL/Queen') : (bed.lowerBedSize === 'twin' ? 'Twin/Twin' : 'Twin/Full'))
            : style === 'inlet'
            ? ['Twin/Twin', 'Twin/Twin']
            : ['bridge', 'rosemary'].includes(style)
              ? [bed.mattressConfig === 'twinxl_queen' ? 'Twin XL/Queen' : 'Twin/Full', bed.mattressConfig === 'twinxl_queen' ? 'Twin XL/Queen' : 'Twin/Full']
              : ['Twin/Full'])
        )
        if (style !== 'inlet' && style !== 'divider') stairs++
      } else {
        const config = CONFIGS[typeof bed.name === 'string' ? bed.name.toLowerCase() : '']
        if (!config) throw new Error('This configuration needs a designer-prepared quote.')
        names.push(config)
      }
    }
    if (!names.length) throw new Error('Add a bunk configuration before choosing a package.')
    totalUnits += names.length
    for (const name of names) {
      // Custom uses the published per-unit proposal price, as the funnel does.
      const amount = productLine === 'custom' ? pricing.configs[name]?.clientPrice : BASIC_BED_PRICE
      if (!Number.isFinite(amount) || amount <= 0)
        throw new Error('This configuration needs a confirmed price.')
      base += amount
      scope.push(`${label}: ${name.replace('/', ' over ')} bunk`)
    }
    const cap = snap.capacity || {},
      occupants: string[] = cap.occupants || [],
      priorities: string[] = cap.priorities || []
    const goal =
      cap.current == null && cap.additional == null
        ? null
        : (cap.current || 0) + (cap.additional || 0)
    const sleeps = names.reduce(
      (sum, name) =>
        sum +
        (['Full/Full', 'Queen/Queen'].includes(name)
          ? 4
          : ['Twin/Full', 'Twin XL/Queen'].includes(name)
            ? 3
            : 2),
      0
    )
    if (goal !== null && sleeps < goal) {
      goalsMet = false
      reasons.push(
        `${label}: this layout sleeps ${sleeps} of your goal of ${goal}. Return to your room to apply the recommended bed configuration before purchasing.`
      )
    }
    const tallest = tallestStatedSleeper(cap)
    if (tallest !== null && tallest + 6 > 80) {
      goalsMet = false
      reasons.push(
        `${label}: the stated ${tallest}″ sleeper needs approximately ${tallest + 6}–${tallest + 12}″ mattress length. Review sizing with your designer; 80″ still falls short of that range.`
      )
    }
    if (
      (priorities.includes('extra_long') || (tallest !== null && tallest + 6 > 75)) &&
      names.some(name => !['Twin XL/Twin XL', 'Twin XL/Queen', 'Queen/Queen'].includes(name))
    ) {
      goalsMet = false
      reasons.push(
        `${label}: your mattress-length goal calls for extra-long mattresses; this selection still contains standard-length beds. Review your room before purchasing.`
      )
    }
    const adultAccess = occupants.some(o => ['adults', 'adult_children', 'teens'].includes(o))
    const needStairs =
      adultAccess || priorities.includes('stairs') || occupants.includes('small_children')
    const bedDrawers = priorities.includes('bed_drawers')
    const storage = cap.storageRequested || priorities.includes('storage') || bedDrawers
    if (adultAccess)
      reasons.push(`${label}: stairs recommended for easier access for adults or older teens.`)
    else if (needStairs && !stairs)
      reasons.push(`${label}: stairs recommended for your stated access needs.`)
    if (storage) reasons.push(`${label}: drawers recommended because you asked for storage.`)
    if (stairs || needStairs || storage) needsPremier = true
    if (productLine === 'basic' && stairs) {
      // Owner-approved complete wholesale price × 1.15.
      const retail = BASIC_UPGRADE_PRICES.stairs
      addonTotal += stairs * retail
      extras.push(`${label}: ${stairs} staircase${stairs > 1 ? 's' : ''} with standard niches (drawers selected separately) · $${(stairs * retail).toLocaleString('en-US')}`)
    } else if (productLine === 'custom' && (stairs || needStairs)) {
      const count = Math.max(stairs, names.length)
      addonTotal += count * addon('staircase_3_drawers')
      const stringerCount = snap.beds.filter((b: any) => b.stairStyle === 'stringer').length
      extras.push(`${label}: staircase access allowance · ${count} bunk units × $${addon('staircase_3_drawers').toLocaleString('en-US')} (shared stairs do not change the per-unit proposal allowance)${stringerCount ? ' · stringer variants exclude stair drawers' : ''}`)
    }
    if (productLine === 'custom' && (storage || snap.beds.some((b: any) => b.kind === 'bridge' || b.kind === 'custom'))) {
      const sets = productLine === 'custom' ? names.length : 1
      addonTotal += sets * addon('storage_drawers_2')
      needsPremier = true
      extras.push(`${label}: under-bed storage drawers · ${sets} sets × $${addon('storage_drawers_2').toLocaleString('en-US')}`)
    }
    // A Custom assembly can contain two complete stacks. Existing X designs are included.
    const bunkCount = snap.beds.reduce(
      (count: number, bed: any) =>
        count +
        (bed.kind === 'stairs'
          ? 0
          : bed.kind === 'bridge' || bed.kind === 'custom'
            ? ['bridge', 'rosemary', 'inlet'].includes(
                bed.kind === 'bridge' ? 'bridge' : bed.customStyle
              )
              ? 2
              : 1
            : 1),
      0
    )
    const includedXCount = snap.beds.reduce(
      (count: number, bed: any) =>
        count +
        (bed.kind === 'custom' && bed.customStyle === 'rosemary'
          ? 2
          : bed.kind === 'custom' && bed.customStyle === 'watercolor'
            ? 1
            : 0),
      0
    )
    // Multi-room snapshots own their selections; legacy fallback is single-room only.
    const selectedStyle =
      snap.guardrailStyle ?? (!plan.layout?.rooms?.length ? plan.layout?.guardrailStyle : undefined)
    const hasCustom = snap.beds.some((bed: any) => bed.kind === 'bridge' || bed.kind === 'custom')
    if (selectedStyle !== undefined && !isBasicGuardrailStyle(selectedStyle))
      throw new Error(
        'Metal or other custom guardrails require a Bliss Custom designer-prepared quote.'
      )
    if (selectedStyle === undefined && hasCustom) {
      scope.push(`${label}: original guardrails included with each Custom layout`)
    } else {
      const style = selectedStyle ?? 'horizontal',
        guardrail = guardrailOption(style)
      if (guardrail.pricePerBunk === null && !options.estimateOnly)
        throw new Error(
          'X-pattern guardrail pricing must be confirmed before choosing a package or paying a deposit.'
        )
      const upgradedCount =
        style === 'x_pattern'
          ? bunkCount - includedXCount
          : style === 'horizontal_3bar'
            ? bunkCount
            : 0
      if (guardrail.pricePerBunk > 0 && upgradedCount > 0) {
        const amount = guardrail.pricePerBunk * upgradedCount
        // Confirmed per-bunk guardrail upgrades stay fixed across Custom tier factors.
        if (productLine === 'custom') customGuardrailTotal += amount
        else addonTotal += amount
        needsPremier = true
        extras.push(
          `${label}: ${guardrailDescription(style)} · ${upgradedCount} × $${guardrail.pricePerBunk.toLocaleString('en-US')} = $${amount.toLocaleString('en-US')}`
        )
        if (style === 'x_pattern' && includedXCount)
          scope.push(
            `${label}: X-pattern rails already included on ${includedXCount} bunk set${includedXCount === 1 ? '' : 's'}`
          )
        standardGuardrails.push(`${label}: ${guardrailDescription('horizontal')}`)
        reasons.push(
          `${label}: ${productLine === 'basic' ? 'Premier includes' : 'Your Custom packages include'} your selected ${style === 'x_pattern' ? 'X-pattern' : 'three-bar'} guardrail upgrade.`
        )
      } else {
        scope.push(
          `${label}: ${guardrailDescription(style)}${style === 'x_pattern' ? ' · included with this layout' : ''}`
        )
      }
    }
    const finish = snap.finish ?? (!plan.layout?.rooms?.length ? plan.layout?.finish : undefined)
    if (productLine === 'basic' && requiresCustomFinish(finish))
      throw new Error('Other finishes, unfinished wood, trim and drawer-pull upgrades require Bliss Custom. Choose Gloss White or the per-bed Custom Color option for Bliss Basic, or continue with Bliss Custom.')
    for (const bed of snap.beds) {
      if (productLine !== 'custom' && (bed.builtInBookshelf || bed.lowerShiplap)) throw new Error('Cabin millwork requires Bliss Custom.')
      for (const line of cabinMillworkPricing(bed,pricing,finish?.treatment === 'unfinished_raw',finish?.crownMolding === 'crown_7' && bed.kind === 'custom' && bed.customStyle === 'divider')) {
        const amount=snap.quoteSettings?.timeline === 'asap' ? Math.round(line.price*1.2) : line.price
        customMillworkTotal+=amount
        scope.push(`${label}: ${line.label} · $${amount.toLocaleString('en-US')} estimated${snap.quoteSettings?.timeline === 'asap' ? ' · includes 20% priority fabrication' : ''}`)
      }
    }
    const paintUpgrade = customPaintUpcharge(finish)
    if (paintUpgrade) {
      customPaintTotal += paintUpgrade
      scope.push(`${label}: Custom paint color upgrade · $${paintUpgrade.toLocaleString('en-US')}`)
    }
    if (hasCustomDetailUpgrades(finish)) {
      const details = roomDetailTakeoff(snap.beds, finish, !!(snap.capacity?.storageRequested && (!snap.beds.some((b:any)=>b.kind==='stairs'&&b.stairStyle!=='stringer') || snap.capacity?.priorities?.includes('bed_drawers'))), bedsideSelections)
      customDetailTotal += details.total
      scope.push(`${label}: Custom trim & drawer hardware · $${details.total.toFixed(2)}${finish.drawerPullStyle ? ' · ' + details.drawers + ' drawer pulls' : ''}`)
    }
    if (finish?.treatment === 'unfinished_raw') scope.push(`${label}: ${roomFinishDescription(finish)}`)
    if (finish?.treatment !== 'unfinished_raw') {
      if (productLine === 'custom') base += 400 * names.length
      if (!bunkFinishes.every((b:any)=>!!b.basicFinish)) scope.push(`${label}: ${roomFinishDescription(finish ?? DEFAULT_ROOM_FINISH)}`)
    }
    const install = names.some(n => ['Twin XL/Queen', 'Queen/Queen'].includes(n))
      ? pricing.install?.mid
      : pricing.install?.standard
    if (productLine === 'custom' && (typeof install !== 'number' || !Number.isFinite(install) || install < 0))
      throw new Error('Installation needs a confirmed price.')
    // Basic's published base and Custom's legacy package include installation.
    if (productLine === 'custom') {
      const subtotal = base - roomBaseStart + addonTotal - roomAddonStart
      const timeline = snap.quoteSettings?.timeline
      if (timeline !== undefined && !['standard', 'asap'].includes(timeline)) throw new Error('Review your project timing.')
      base += customFabricationTotal(subtotal, timeline) - subtotal
      if (timeline === 'asap') scope.push(`${label}: 20% priority build & expedited fabrication fee included`)
    }
  }
  if (productLine === 'custom' && !extras.length) {
    addonTotal = addon('storage_drawers_2')
    extras.push(`${rooms[0].name || 'Your room'}: under-bed storage drawers`)
  }
  const promotion = basicPromotion(options.promoCode, productLine, totalUnits);
  if (promotion) {
    base -= promotion.savings;
    scope.push(`BEDOCTOBER: 25% off Basic base bunk sets · savings $${promotion.savings.toFixed(2)} · extras and delivery excluded`);
  }
  const deliveryZip = planDeliveryZip(plan, zip)
  const delivery = options.estimateOnly && !deliveryZip ? {deliveryFee:0} : calculateBlissDeliveryFromZip(deliveryZip, totalUnits)
  base += delivery.deliveryFee
  scope.push(
    'Professional installation',
    deliveryZip ? `Delivery allowance basis: ${delivery.deliveryFee.toLocaleString()} · included in package pricing` : 'Delivery allowance pending jobsite ZIP'
  )
  const summary = scope.filter(s => s.includes(' bunk')).join(' · ')
  const preferredWindows: Record<string,string> = {soon:'As soon as available', '1_2_months':'Within 1–2 months', '3_4_months':'Within 3–4 months', flexible:'Flexible'}
  const common = {
    ...(promotion ? {promotion} : {}),
    productLine,
    planId: plan.id,
    reasons: [...new Set(reasons)],
    summary,
    rooms: rooms.length,
    reviewItems,
    exclusions: [...PLAN_EXCLUSIONS],
    deliveryZip: planDeliveryZip(plan, zip),
    timing: rooms.map((room: any, index: number) => `${room.name || `Room ${index + 1}`}: ${preferredWindows[room.configuratorSnapshot?.quoteSettings?.preferredWindow] ? `Preferred installation: ${preferredWindows[room.configuratorSnapshot.quoteSettings.preferredWindow]} · ` : ''}${productLine === 'custom' && room.configuratorSnapshot?.quoteSettings?.timeline === 'asap' ? 'ASAP requested · 20% fabrication surcharge included; installation scheduled with you after fabrication is complete' : 'Standard scheduling · installation scheduled with you after fabrication is complete'}`),
  }
  if (productLine === 'basic')
    return {
      ...common,
      packages: [
        {
          id: 'standard',
          label: 'Standard',
          total: base,
          scope: [...scope, ...standardGuardrails, 'Ladder access · no add-ons'],
          recommended: goalsMet && !needsPremier,
        },
        ...(addonTotal > 0 ? [{
          id: 'premier',
          label: 'Premier',
          total: base + addonTotal,
          scope: [...scope, ...extras],
          recommended: goalsMet && needsPremier,
        }] : []),
      ],
    }
  // Custom retains the existing Essential / Reserve / Signature price factors.
  const reserve = customReserveBaseline(base - delivery.deliveryFee + (needsPremier ? addonTotal : 0), delivery.deliveryFee)
  return {
    ...common,
    packages: (['signature', 'reserve', 'essential'] as const).map(id => ({
      id,
      label: BLISS_LUXURY_TIER_PRESENTATION[id].displayName,
      total: proposalTierTotal(reserve, id) + customGuardrailTotal + customBedsideTotal + customDetailTotal + customPaintTotal + customMillworkTotal,
      recommended: goalsMet && id === 'signature',
      scope: [
        ...scope,
        ...(needsPremier ? extras : []),
        ...(
          BLISS_LUXURY_TIER_PRESENTATION[id].bullets || [
            BLISS_LUXURY_TIER_PRESENTATION[id].description!,
          ]
        ),
      ],
    })),
  }
}

/** Priced build agreement; optional unpriced selections remain saved outside its scope. */
export function buildPlanCheckoutPackages(...args: Parameters<typeof buildPlanPackages>): PlanProposal {
  const proposal = buildPlanPackages(args[0], args[1], args[2], args[3], args[4], { ...args[5], estimateOnly: true })
  if (proposal.reviewItems?.some(item => /staircase/i.test(item))) throw new Error('Your staircase selling price needs approval before checkout. Request a completed quote; stair drawers are not included.')
  if (proposal.reviewItems?.some(item => /guardrail/i.test(item))) throw new Error('Your selected guardrail needs a confirmed price before checkout. Choose a priced guardrail or contact Bliss.')
  const separatelyQuotedSelections = [...new Set(proposal.reviewItems ?? [])]
  return { ...proposal, reviewItems: [], separatelyQuotedSelections,
    packages: proposal.packages.map(pkg => ({ ...pkg, scope: [ ...pkg.scope,
      ...separatelyQuotedSelections.map(item => `Separately quoted — excluded from this package and deposit: ${item}`)
    ] })) }
}










